<script setup lang="ts">
import AppLayout from '../../layouts/AppLayout.vue'
import { ref, onMounted, computed } from 'vue'
import { useAuth } from '../../composables/useAuth'
import { getSupplierTrainingAssets } from '../../services/api'
import { useCurrentContext } from '../../composables/useCurrentContext'

const { supplierId } = useCurrentContext()
const { getAccessTokenSilently } = useAuth()
const loading = ref(true)
const assets = ref<any[]>([])
const search = ref('')

onMounted(async () => {
  try {
    if (!supplierId.value) throw new Error('No supplier context')
    const token = await getAccessTokenSilently()
    assets.value = await getSupplierTrainingAssets(supplierId.value, token)
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
})

const filteredAssets = computed(() => {
  if (!search.value) return assets.value
  return assets.value.filter(a => a.title.toLowerCase().includes(search.value.toLowerCase()))
})
</script>

<template>
  <AppLayout>
    <div class="space-y-6">
      <div class="flex justify-between items-center">
        <div>
           <h1 class="text-2xl font-bold text-cc-text">Training Library</h1>
           <p class="text-cc-text-secondary">Enablement content for your advisors.</p>
        </div>
        <div class="flex gap-2">
           <input v-model="search" type="text" placeholder="Search content..." class="border border-cc-border-med bg-cc-elevated text-cc-text rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none" />
           <router-link to="/supplier/training/new" class="bg-cc-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-cc-primary-hover transition">
               + Add Resource
           </router-link>
        </div>
      </div>

      <div v-if="loading" class="text-center py-12 text-cc-text-secondary">Loading library...</div>

      <div v-else class="bg-cc-elevated rounded-xl border border-cc-border overflow-hidden shadow-sm">
        <table class="w-full text-left">
            <thead class="bg-gray-50 dark:bg-gray-700/50 border-b border-cc-border">
                <tr>
                    <th class="px-6 py-3 text-xs font-bold text-cc-text-secondary uppercase">Resource Title</th>
                    <th class="px-6 py-3 text-xs font-bold text-cc-text-secondary uppercase">Type</th>
                    <th class="px-6 py-3 text-xs font-bold text-cc-text-secondary uppercase">Linked Products</th>
                    <th class="px-6 py-3 text-xs font-bold text-cc-text-secondary uppercase text-right">Actions</th>
                </tr>
            </thead>
            <tbody class="divide-y divide-cc-border">
                <tr v-for="asset in filteredAssets" :key="asset.id" class="hover:bg-cc-hover transition">
                    <td class="px-6 py-4">
                        <div class="font-bold text-cc-text mb-1">{{ asset.title }}</div>
                        <div class="text-xs text-cc-text-secondary truncate max-w-xs">{{ asset.url }}</div>
                    </td>
                    <td class="px-6 py-4">
                        <span :class="['px-2 py-1 rounded text-xs font-bold uppercase', asset.asset_type === 'Video' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300']">
                            {{ asset.asset_type }}
                        </span>
                    </td>
                    <td class="px-6 py-4 text-sm text-cc-text-secondary">
                         <span v-if="asset.products && asset.products.length">
                            {{ asset.products.map((p:any) => p.name).join(', ') }}
                        </span>
                        <span v-else class="text-gray-400 italic">General</span>
                    </td>
                    <td class="px-6 py-4 text-right">
                        <a :href="asset.url" target="_blank" class="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium text-sm">View</a>
                    </td>
                </tr>
            </tbody>
        </table>
      </div>
    </div>
  </AppLayout>
</template>
